import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch {
      return null;
    }
  });

  const [systemIsDark, setSystemIsDark] = useState(
    () => window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemIsDark(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  const [themePref, setThemePref] = useState(() => {
    if (user) return localStorage.getItem('theme') || user.theme_mode || 'system';
    return 'system';
  });

  // Logged out = always follow the system theme; logged in = respect the user's choice.
  const isDark = !user ? systemIsDark : themePref === 'system' ? systemIsDark : themePref === 'dark';

  useEffect(() => {
    document.body.className = isDark ? 'dark' : 'light';
  }, [isDark]);

  const [notification, setNotification] = useState(null);

  const notify = useCallback((message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  }, []);

  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      api.get('/session').catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, [user]);

  const login = useCallback((u) => {
    setUser(u);
    localStorage.setItem('user', JSON.stringify(u));
    setThemePref(localStorage.getItem('theme') || u.theme_mode || 'system');
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/logout');
    } catch {
      // Best-effort - the token is removed client-side regardless.
    }
    setUser(null);
    localStorage.removeItem('user');
    setThemePref('system');
    notify('Logged out', 'success');
  }, [notify]);

  const changeTheme = useCallback(
    async (newMode) => {
      setThemePref(newMode);
      localStorage.setItem('theme', newMode);
      if (user) {
        setUser((prev) => {
          const updated = { ...prev, theme_mode: newMode };
          localStorage.setItem('user', JSON.stringify(updated));
          return updated;
        });
        try {
          await api.put('/user/profile', { theme_mode: newMode });
        } catch {
          // Best-effort sync - local preference still applies immediately.
        }
      }
    },
    [user]
  );

  const updateUser = useCallback((patch) => {
    setUser((prev) => {
      const updated = { ...prev, ...patch };
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const value = {
    user,
    login,
    logout,
    updateUser,
    isDark,
    themePref,
    changeTheme,
    notify,
    notification,
    clearNotification: () => setNotification(null),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
